import { useMemo } from 'react';
import { cApi, Identity, MakeCb } from 'src/api';
import { Scope } from 'src/api/Enums';
import { useMakeQuery } from 'src/hooks/useMakeQuery';

/**
 * Single source for preference loading: fires the per-scope queries once and exposes
 * both the hierarchically-merged map (for React) and the raw per-scope maps (`byScope`,
 * e.g. to feed the Backbone PreferenceHandler), plus a real `isLoading` flag.
 *
 * @param {Scope} [scope]
 * @returns {object} { preferences, byScope, isLoading }
 */
export const usePreferencesQuery = (scope = Scope.User) => {

  const userAccountId = scope === Scope.User ? Identity.userAccountId : null;

  const config = { keepPreviousData: true, refetchOnMount: false, staleTime: 60 * 60 * 1000 }; // 1 hour

  const { data: globalPreferences } = useMakeQuery(MakeCb.get.one(cApi.preferences, Scope.Global),
      { ...config, staleTime: 24 * 60 * 60 * 1000 }); // 1 day
  const { data: companyPreferences } = useMakeQuery(MakeCb.get.one(cApi.preferences, Scope.Company), { ...config, enabled: scope !== Scope.Global });

  const { data: userPreferences } = useMakeQuery(MakeCb.get.one(cApi.preferences, Scope.User, { query: { userAccountId } }),
    { ...config, enabled: !!userAccountId });

  // A scope is pending while its data is undefined (an empty result is `{}`, which is truthy).
  const isLoading = !globalPreferences
    || (scope !== Scope.Global && !companyPreferences)
    || (!!userAccountId && !userPreferences);

  const byScope = useMemo(() => ({
    [Scope.Global]: globalPreferences,
    [Scope.Company]: companyPreferences,
    [Scope.User]: userAccountId ? userPreferences : undefined,
  }), [globalPreferences, companyPreferences, userPreferences]);

  const preferences = useMemo(() => {
    if (isLoading) {
      return {};
    }
    return Object.assign({}, globalPreferences, companyPreferences, (userAccountId ? userPreferences : {}));
  }, [globalPreferences, companyPreferences, userPreferences]);

  return { preferences, byScope, isLoading };
};

/**
 * Getting preferences hierarchically from provided scope and up.
 * @param {Scope} [scope]
 * @returns {Record<string, string>}
 */
export const usePreferences = (scope = Scope.User) => usePreferencesQuery(scope).preferences;

export default usePreferences;
